// Unrecognised params are silently ignored by the server, so streaming.test.ts asserts exact names.
import { SAMPLE_RATE } from "@components/Transcriber/streaming";

export const ASSEMBLYAI_WS_URL = "wss://streaming.assemblyai.com/v3/ws";

// Always emits formatted finals, so no `format_turns` param is needed.
export const SPEECH_MODEL = "universal-3-5-pro";

// Bounds labelling lost on a dropped connection; the end-of-session revision only
// arrives on a graceful Terminate. Server minimum is 120000.
export const SPEAKER_LABELS_REVISION_INTERVAL_MS = 300000;

// Streaming diarization hard cap.
export const MAX_SPEAKERS_LIMIT = 10;

// Insurance against any server-side idle policy while paused (no audio is sent).
export const KEEPALIVE_INTERVAL_MS = 15000;

// Re-fetch a token this often so the backend's 15-min sliding consent window never lapses mid-session.
export const CONSENT_REFRESH_INTERVAL_MS = 9 * 60 * 1000;

export const TERMINATE_DRAIN_TIMEOUT_MS = 3000;

export const LOW_REALTIME_FACTOR_THRESHOLD = 0.8;

// The server defaults this OFF when speaker labels are on, leaving ~one partial per sentence.
export const CONTINUOUS_PARTIALS = true;

// Revert to "balanced" if live-feed accuracy suffers.
export const STREAMING_MODE = "min_latency";

export type AssemblyAIStreamingUrlOptions = {
  speakerLabels?: boolean;
  // Hard cap: extra voices merge into the nearest label.
  maxSpeakers?: number;
  domain?: string | null;
};

export function clampMaxSpeakers(value: number | undefined | null): number | null {
  if (value == null || !Number.isFinite(value) || value < 1) return null;
  return Math.min(Math.max(1, Math.round(value)), MAX_SPEAKERS_LIMIT);
}

export function buildAssemblyAIStreamingUrl(
  token: string,
  options: AssemblyAIStreamingUrlOptions = {}
): string {
  const params = new URLSearchParams({
    speech_model: SPEECH_MODEL,
    sample_rate: String(SAMPLE_RATE),
    encoding: "pcm_s16le",
    // Auto-detect so interpreter-assisted (code-switched) assessments aren't forced to English.
    language_detection: "true",
    // Primary dead-connection signal, since a quiet room produces no Turn events.
    session_heartbeat: "true",
    mode: STREAMING_MODE,
    continuous_partials: String(CONTINUOUS_PARTIALS),
  });

  if (options.domain) {
    params.set("domain", options.domain);
  }

  if (options.speakerLabels) {
    params.set("speaker_labels", "true");
    params.set(
      "speaker_labels_revision_interval_ms",
      String(SPEAKER_LABELS_REVISION_INTERVAL_MS)
    );
    const maxSpeakers = clampMaxSpeakers(options.maxSpeakers);
    if (maxSpeakers != null) {
      params.set("max_speakers", String(maxSpeakers));
    }
  }

  // Token last so it's easy to redact from any logged URL.
  params.set("token", token);
  return `${ASSEMBLYAI_WS_URL}?${params.toString()}`;
}
